import { FC, useState } from 'react';
import { GUsersTableProps } from '../items/interfaces/IUsers';
import { EditButton, DeleteButton } from '../utils/buttons';
import ConfirmForm from '../dialogs/ConfirmForm';

/**
 * GRolesTable
 * Renders a table of role objects with edit controls.
 * @component
 * @param {GUsersTableProps} props - Props containing roles data and edit handlers.
 * @returns {JSX.Element} Table of roles with edit controls.
 */
const GRolesTable: FC<GUsersTableProps> = ({
    users,
    editRow,
    editValues,
    setEditRow,
    setEditValues,
    handleEditSave,
    handleEditRole,
    handleDelete,
    roles,
    rolesdesks,
}) => {
    const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; role: any | null }>({
        open: false,
        role: null,
    });

    // rolesdesks may include desk assignments; keep raw access for handlers when needed

    return (
        <>
            <table className='elegant-table' border={1} cellPadding={2}>
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>Role</th>
                        <th style={{ width: '120px' }}>Edit</th>
                    </tr>
                </thead>
                <tbody>
                    {(roles || []).map((role, idx) => (
                        <tr key={idx}>
                            {editRow === idx ? (
                                <>
                                    <td>{role.id}</td>
                                    <td>
                                        <input
                                            type='text'
                                            value={editValues.role ?? role.role}
                                            onChange={(e) => setEditValues({ ...editValues, role: e.target.value })}
                                        />
                                    </td>
                                    <td>
                                        <button className='icon-blue'>Save</button>
                                        <button className='icon-blue' onClick={() => setEditRow(null)}>
                                            Cancel
                                        </button>
                                        {handleDelete && (
                                            <button
                                                className='icon-red'
                                                onClick={() => setDeleteConfirm({ open: true, role })}
                                            >
                                                Delete
                                            </button>
                                        )}
                                    </td>
                                </>
                            ) : (
                                <>
                                    <td>{role.id}</td>
                                    <td>{role.role}</td>
                                    <td style={{ width: '120px' }}>
                                        <EditButton
                                            onClick={() => {
                                                if (handleEditRole) {
                                                    // Get desks for this role
                                                    const roleDesks = (rolesdesks || [])
                                                        .filter((rd: any) => rd.id_role === role.id)
                                                        .map((rd: any) => {
                                                            try {
                                                                const desks = JSON.parse(rd.desk);
                                                                return Array.isArray(desks) ? desks : [rd.desk];
                                                            } catch {
                                                                return [rd.desk];
                                                            }
                                                        })
                                                        .flat();
                                                    handleEditRole(role.role, roleDesks);
                                                } else {
                                                    setEditRow(idx);
                                                    setEditValues({ id: role.id, role: role.role, desk: role.desk });
                                                }
                                            }}
                                        />
                                        {handleDelete && (
                                            <DeleteButton onClick={() => setDeleteConfirm({ open: true, role })} />
                                        )}
                                    </td>
                                </>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
            <ConfirmForm
                open={deleteConfirm.open}
                title='Confirm Deletion'
                content={`Are you sure you want to delete role ${deleteConfirm.role?.role}?`}
                onCancel={() => setDeleteConfirm({ open: false, role: null })}
                onConfirm={async () => {
                    if (deleteConfirm.role && handleDelete) {
                        await handleDelete({
                            user: deleteConfirm.role.role,
                            role: deleteConfirm.role.role,
                            desks: [],
                        });
                        setEditRow(null);
                    }
                    setDeleteConfirm({ open: false, role: null });
                }}
            />
        </>
    );
};

export default GRolesTable;
